import { DOCUMENT, DestroyRef, Injectable, RendererFactory2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, filter, map, startWith } from 'rxjs';

import { hasProp } from '@dmb/core';

import { DmbFontSize } from '../model';
import { UserService } from '../user';

export type DmbFrameEventType =
  | 'frame-ready'
  | 'frame-unload'
  | 'font-size-update'
  | 'handshake-response'
  | 'height-update'
  | 'overlay-active'
  | 'overlay-request'
  | 'overlay-status'
  | 'selected-client-update'
  | 'page-title'
  | 'impersonation-request'
  | 'v4-navigation'
  | 'popup-open'
  | 'popup-center-add'
  | 'popup-center-close'
  | 'popup-center-action'
  | 'matomo-event';

export interface DmbFrameEvent<T> {
  eventType: DmbFrameEventType;
  payload: T;
}

export type DmbFrameIsReady = DmbFrameEvent<{ url: string; path: string }>;
export type DmbFrameUnload = DmbFrameEvent<boolean>;
export type DmbV4Navigation = DmbFrameEvent<string>;
export type DmbFrameHeight = DmbFrameEvent<number>;
export type DmbOverlayActive = DmbFrameEvent<boolean>;
export type DmbFontSizeUpdate = DmbFrameEvent<DmbFontSize>;
export type DmbPageTitle = DmbFrameEvent<string>;
export type DmbImpersonation = DmbFrameEvent<number>;
export type DmbPopupOpen = DmbFrameEvent<{ msg: string; title?: string }>;
export type DmbMatomoEvent = DmbFrameEvent<{ category: string; action: string; name?: string }>;

export type PopupCenterAddPayload = {
  type: 'info' | 'error' | 'warning';
  title: string;
  message?: string;
  timeout?: number; // Milliseconds
} & (
  | {
      id?: string;
    }
  | {
      id: string;
      // If action is clicked, a 'popup-center-action' message, with the id as payload, is sent back.
      actionButtonText: string;
    }
);
export type DmbPopupCenterAdd = DmbFrameEvent<PopupCenterAddPayload | PopupCenterAddPayload[]>;
export type DmbPopupCenterClose = DmbFrameEvent<string | string[]>;

export const isFrameEvent =
  <T>(prop: DmbFrameEventType) =>
  (e: unknown): e is T =>
    hasProp(e, 'eventType') && typeof e.eventType === 'string' && e.eventType === prop;

export const isFrameIsReady = isFrameEvent<DmbFrameIsReady>('frame-ready');
export const isFrameUnload = isFrameEvent<DmbFrameUnload>('frame-unload');
export const isOverlayActive = isFrameEvent<DmbOverlayActive>('overlay-active');
export const isFrameHeight = isFrameEvent<DmbFrameHeight>('height-update');
export const isV4Navigation = isFrameEvent<DmbV4Navigation>('v4-navigation');
export const isPageTitle = isFrameEvent<DmbPageTitle>('page-title');
export const isImpersonation = isFrameEvent<DmbImpersonation>('impersonation-request');
export const isPopupOpen = isFrameEvent<DmbPopupOpen>('popup-open');
export const isPopupCenterAdd = isFrameEvent<DmbPopupCenterAdd>('popup-center-add');
export const isPopupCenterClose = isFrameEvent<DmbPopupCenterClose>('popup-center-close');
export const isMatomoEvent = (e: unknown): e is DmbMatomoEvent =>
  isFrameEvent<DmbMatomoEvent>('matomo-event')(e) &&
  hasProp(e.payload, 'category') &&
  typeof e.payload.category === 'string' &&
  hasProp(e.payload, 'action') &&
  typeof e.payload.action === 'string';

@Injectable({
  providedIn: 'root',
})
export class FrameService {
  private prefs = inject(UserService);
  private destroyRef = inject(DestroyRef);

  messages = new Subject<DmbFrameEvent<unknown>>();

  frameAvailable = this.messages.pipe(
    filter((e) => isFrameIsReady(e) || isFrameUnload(e)),
    map(isFrameIsReady),
    startWith(false),
  );

  outbox = new Subject<DmbFrameEvent<unknown>>();

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    // During local development, some messages have a port, some don't
    const portlessOrigin = this.stripPort(inject(DOCUMENT).location.origin);

    const renderer = rendererFactory.createRenderer(null, null);
    renderer.listen('window', 'message', (m: MessageEvent) => {
      if (this.stripPort(m.origin) === portlessOrigin) {
        this.messages.next(m.data);
      } else {
        console.warn('Received message from an unknown origin', m);
      }
    });

    this.listenForFontSizeChanges();
  }

  postMessage<T>(eventType: DmbFrameEventType, payload: T | null = null) {
    this.outbox.next({ eventType, payload });
  }

  listenForFontSizeChanges() {
    this.prefs.fontSize
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(
          (payload): DmbFontSizeUpdate => ({
            eventType: 'font-size-update',
            payload,
          }),
        ),
      )
      .subscribe((e) => this.outbox.next(e));
  }

  private stripPort(origin: string): string {
    return origin.replace(/:\d+$/, '');
  }
}
